import React from 'react';

import { useLocation } from 'react-router';

import { canOfferBeRedeemed } from 'components/CancelSubscription/Offers/offers';
import Message from 'components/Message';
import TrialProgressBar from 'components/TrialProgressBar';
import Typography from 'components/Typography';

import ReactivateSubscription from 'containers/Account/ReactivateSubscription';

import {
  ActiveSubscriptionFragment,
  RedeemedOfferType,
  useGetRedeemedOffersQuery,
} from 'graphql/generated/graphql';

import { isInTrial } from 'helpers/subscription';

import { useAuth } from 'hooks/useAuth';
import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import './style.scss';

const TrialProgressBarWithReactivation = ({
  activeSubscription,
  subscriptionInTrial,
}: {
  activeSubscription: ActiveSubscriptionFragment;
  subscriptionInTrial: boolean;
}) => {
  const { data: dataRedeemedOffers } = useGetRedeemedOffersQuery({
    ...useGraphaelDefaults(),
    fetchPolicy: 'cache-and-network',
  });

  const shouldApplyCoupon = canOfferBeRedeemed(
    RedeemedOfferType.Discount,
    dataRedeemedOffers?.redeemedOffers,
  );

  const getDescriptionTranslationKey = () => {
    if (!subscriptionInTrial) {
      return 'components.ReactivateSubscription.descriptionNotInTrial';
    }

    return shouldApplyCoupon
      ? 'components.ReactivateSubscription.descriptionWithoutCoupon'
      : 'components.ReactivateSubscription.descriptionWithCoupon';
  };

  const descriptionTranslationKey = getDescriptionTranslationKey();

  return (
    <div className="TrialProgressBarWithReactivation">
      {subscriptionInTrial && (
        <TrialProgressBar
          activeSubscription={activeSubscription}
          variant="small-thick"
          color="dark-body"
          centered
        />
      )}
      {subscriptionInTrial && shouldApplyCoupon && (
        <Typography variant="h3" color="dark-body" centered>
          <em>
              <Message
              message={{ id: 'components.ReactivateSubscription.timeLeft' }}
            />
          </em>
          <Message
            message={{ id: 'components.ReactivateSubscription.discount' }}
          />
        </Typography>
      )}

      {!shouldApplyCoupon && (
        <Typography variant="h3" color="dark-body" centered>
          <Message
            message={{
              id: 'components.ReactivateSubscription.titleWithCoupon',
            }}
          />
        </Typography>
      )}
      <Typography variant="body" color="dark-body" component="p" centered>
        <Message message={{ id: descriptionTranslationKey }} />
      </Typography>
      <ReactivateSubscription
        applyCoupon={shouldApplyCoupon}
        trialCtaTranslationKey={
          shouldApplyCoupon
            ? 'components.ReactivateSubscription.applyCoupon'
            : undefined
        }
      />
    </div>
  );
};

const TrialProgressBarWrapper = () => {
  const { activeSubscription } = useAuth();

  const { pathname } = useLocation();

  const isScheduledForCancellation =
    !!activeSubscription?.cancellationScheduledAt;
  const ignoredPathname =
    pathname.includes('cancel-subscription') ||
    pathname.includes('subscription-reactivation-success');
  const subscriptionInTrial = isInTrial(activeSubscription?.freeTrialEndsAt);

  if (isScheduledForCancellation && !ignoredPathname) {
    return (
      <TrialProgressBarWithReactivation
        activeSubscription={activeSubscription}
        subscriptionInTrial={subscriptionInTrial}
      />
    );
  }

  return null;
};

export default TrialProgressBarWrapper;
